import { FLAGS } from '@iheartjane/feature-flags';
import {
  CompareProducts,
  JaneGoldUserCallout,
  WizardButton,
} from '@iheartjane/menu';
import type { LabeledOption } from '@iheartjane/types';
import { useMemo, useRef } from 'react';

import { JaneGoldFilter } from '@jane/bloom/components/productListingPage/categoryAndFilterSelectors/filterSelector/janeGold';
import {
  useBloomFilters,
  useSearchParamFilters,
} from '@jane/bloom/hooks/filters';
import { useFullDestination } from '@jane/bloom/hooks/navigation/useFullDestination';
import { useRouteWithRedirect } from '@jane/bloom/hooks/useRedirectRoute';
import { useRootLoaderData } from '@jane/bloom/hooks/useRootLoaderData';
import type { SortOptions } from '@jane/bloom/types/schemas/algolia/product';
import { isJaneUser } from '@jane/bloom/types/schemas/marketplace/janeUser';
import { getFilterChips } from '@jane/bloom/utils/filters/getFilterChips';
import { isEmpty } from '@jane/bloom/utils/guards/isEmpty';

import { SearchDropdown } from '../../search/searchDropdown';
import { SearchFieldButton } from '../../search/searchFieldButton';
import { FilterButtonWrapper } from './filterButtonWrapper';
import { AllFiltersModal } from './filterSelector/allFiltersModal';
import { FilterCategoriesButton } from './filterSelector/categories';
import { FilterCheckboxList } from './filterSelector/checkboxList';
import { FilterFeelingsButtonList } from './filterSelector/feelings';
import { FilterWithSearchButton } from './filterSelector/filterWithSearch';
import { FilterPotenciesButton } from './filterSelector/potencies';
import { FilterRatingsButton } from './filterSelector/ratings';
import { SelectedFilterTags } from './filterSelector/selectedFilterTags';
import { SortSelector } from './filterSelector/sortSelector';
import { useHideOverflowItems } from './useHideOverflowItems';

import styles from './filterSelector.module.css';

type FilterSelectorProps = {
  availableProductsCount?: number;
  currentSort: SortOptions;
  hasJaneGoldProducts?: boolean;
  hideLabeledFilters?: boolean;
  hideSearch?: boolean;
  onWizardClick?: () => void;
  shouldShowGold?: boolean;
  showCompare?: boolean;
  sortOptions: LabeledOption<SortOptions>[];
};

export function FilterSelector({
  availableProductsCount,
  currentSort,
  hasJaneGoldProducts,
  hideLabeledFilters,
  hideSearch,
  onWizardClick,
  shouldShowGold,
  showCompare = false,
  sortOptions,
}: FilterSelectorProps) {
  const { featureFlags, pinnedStore, theme, whoami } = useRootLoaderData();
  const useCustomComponents = featureFlags?.[FLAGS.c2CustomComponents] ?? false;
  const showSearchField =
    !hideSearch &&
    !!useCustomComponents &&
    theme?.bloom?.search?.position === 'filters';
  const authenticated = isJaneUser(whoami.user);
  const getRouteWithRedirect = useRouteWithRedirect();
  const generatePath = useFullDestination();
  const filterButtonsRef = useRef<HTMLDivElement>(null);
  useHideOverflowItems(filterButtonsRef);

  const { availableFilters, sortedDisplayFilterKeys } = useBloomFilters();
  const filterChips = useMemo(
    () =>
      getFilterChips({
        availableFilters,
        filters: sortedDisplayFilterKeys,
        janeGold: shouldShowGold,
      }),
    [availableFilters, shouldShowGold, sortedDisplayFilterKeys]
  );

  const { getSelectedFilterValues } = useSearchParamFilters('hasBrandDiscount');

  const showGoldCallout =
    shouldShowGold &&
    pinnedStore.state === 'Ohio' &&
    getSelectedFilterValues().some(
      (filter) => filter.filterKey === 'hasBrandDiscount'
    );

  return (
    <nav className={styles['productFilterSelector']}>
      <div className={styles['productFilterContainer']}>
        <div ref={filterButtonsRef} className={styles['filterButtons']}>
          {showSearchField && (
            <>
              <div>
                <SearchDropdown />
                <SearchFieldButton />
              </div>
              {onWizardClick && <WizardButton onClickStart={onWizardClick} />}
            </>
          )}
          {!isEmpty(sortedDisplayFilterKeys) && (
            <AllFiltersModal
              hasJaneGoldProducts={hasJaneGoldProducts}
              availableProductsCount={availableProductsCount}
              sortOptions={sortOptions}
              currentSort={currentSort}
              shouldShowGold={shouldShowGold}
            />
          )}
          {!!useCustomComponents &&
            theme?.bloom?.search?.position === 'split' &&
            onWizardClick && <WizardButton onClickStart={onWizardClick} />}
          {!hideLabeledFilters &&
            !showSearchField &&
            filterChips.map((filterKey) => {
              switch (filterKey) {
                case 'jane-gold':
                  return hasJaneGoldProducts ? (
                    <JaneGoldFilter key="jane-gold" />
                  ) : null;
                case 'brands':
                case 'terpenes':
                  return (
                    <FilterButtonWrapper key={filterKey} filterKey={filterKey}>
                      <FilterWithSearchButton
                        filterKey={filterKey}
                        availableProductsCount={availableProductsCount}
                      />
                    </FilterButtonWrapper>
                  );
                case 'categories':
                  return (
                    <FilterButtonWrapper key={filterKey} filterKey={filterKey}>
                      <FilterCategoriesButton
                        availableProductsCount={availableProductsCount}
                      />
                    </FilterButtonWrapper>
                  );
                case 'ratings':
                  return (
                    <FilterButtonWrapper key={filterKey} filterKey={filterKey}>
                      <FilterRatingsButton
                        availableProductsCount={availableProductsCount}
                      />
                    </FilterButtonWrapper>
                  );
                case 'feelings':
                  return (
                    <FilterButtonWrapper key={filterKey} filterKey={filterKey}>
                      <FilterFeelingsButtonList
                        availableProductsCount={availableProductsCount}
                      />
                    </FilterButtonWrapper>
                  );
                case 'potencies':
                  return (
                    <FilterButtonWrapper key={filterKey} filterKey={filterKey}>
                      <FilterPotenciesButton
                        availableProductsCount={availableProductsCount}
                      />
                    </FilterButtonWrapper>
                  );
                default:
                  return (
                    <FilterButtonWrapper key={filterKey} filterKey={filterKey}>
                      <FilterCheckboxList
                        availableProductsCount={availableProductsCount}
                        filterKey={filterKey}
                      />
                    </FilterButtonWrapper>
                  );
              }
            })}
        </div>
        <div className={styles['sortAndAction']}>
          {!useCustomComponents && onWizardClick && (
            <WizardButton onClickStart={onWizardClick} />
          )}
          {!useCustomComponents && showCompare && <CompareProducts.Button />}
          <SortSelector sortOptions={sortOptions} currentSort={currentSort} />
        </div>
      </div>
      <SelectedFilterTags />
      {showGoldCallout && (
        <JaneGoldUserCallout
          onLinkAccountTo={generatePath(getRouteWithRedirect('/login'))}
          storeState={pinnedStore.state}
          userSignedIn={!!authenticated}
          userHasLinkedBank={true} //TODO: check bank account status when bank linking flow is fully functional in bloom
          mb={24}
        />
      )}
    </nav>
  );
}
